import * as RadixToast from '@radix-ui/react-toast';
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface ToastItem { id: number; kind: 'success' | 'error'; title: string; description?: string }
interface ToastCtx { push: (t: Omit<ToastItem, 'id'>) => void }
const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((t: Omit<ToastItem, 'id'>) => {
    setItems((prev) => [...prev, { ...t, id: Date.now() + Math.random() }]);
  }, []);
  const dismiss = (id: number) => setItems((prev) => prev.filter((t) => t.id !== id));
  return (
    <Ctx.Provider value={{ push }}>
      <RadixToast.Provider swipeDirection="right">
        {children}
        {items.map((t) => (
          <RadixToast.Root
            key={t.id}
            className={`toast toast-${t.kind}`}
            duration={t.kind === 'error' ? 7000 : 5000}
            onOpenChange={(open) => { if (!open) dismiss(t.id); }}
          >
            <RadixToast.Title className="toast-title">{t.title}</RadixToast.Title>
            {t.description && <RadixToast.Description className="toast-desc">{t.description}</RadixToast.Description>}
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className="toast-viewport" />
      </RadixToast.Provider>
    </Ctx.Provider>
  );
}

export function useToast(): ToastCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
